import { useEffect, useState } from "react";

import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../../firebase";

import { useAuth } from "../../context/AuthContext";

import {
  defectiveReturn
} from "../../services/inventoryService";

export default function DefectiveReturn() {

  const { currentUser } =
    useAuth();

  const [issues, setIssues] =
    useState([]);

  const [
    selectedIssue,
    setSelectedIssue
  ] = useState(null);

  const [lineItems, setLineItems] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [form, setForm] =
    useState({

      issue_id: "",

      defect_reason: "",

      notes: "",
    });

  useEffect(() => {

    if (
      currentUser?.company_id
    ) {

      fetchDirectIssues();
    }

  }, [currentUser]);

  // =================================================
  // FETCH DIRECT ISSUES
  // =================================================

  const fetchDirectIssues =
    async () => {

      try {

        const snapshot =
          await getDocs(

            query(

              collection(
                db,
                "direct_issues"
              ),

              where(
                "company_id",
                "==",
                currentUser.company_id
              )
            )
          );

        setIssues(

          snapshot.docs.map(
            (doc) => ({

              id:
                doc.id,

              ...doc.data(),
            })
          )
        );

      } catch (error) {

        console.error(
          "Error fetching issues:",
          error
        );
      }
    };

  // =================================================
  // GENERATE DEFECTIVE NUMBER
  // =================================================

  const generateDefectiveNo =
    async () => {

      const snapshot =
        await getDocs(

          query(

            collection(
              db,
              "defective_returns"
            ),

            where(
              "company_id",
              "==",
              currentUser.company_id
            )
          )
        );

      return `DEF-${String(

        snapshot.size + 1

      ).padStart(4, "0")}`;
    };

  // =================================================
  // HANDLE ISSUE SELECT
  // =================================================

  const handleIssueSelect =
    (issueId) => {

      const issue =
        issues.find(
          (item) =>
            item.id === issueId
        );

      if (!issue) return;

      setSelectedIssue(
        issue
      );

      setForm({

        ...form,

        issue_id:
          issue.id,
      });

      setLineItems(

        (issue.items || [])
          .map((item) => ({

            item_id:
              item.item_id,

            item_code:
              item.item_code || "",

            category:
              item.category || "",

            item_name:
              item.item_name,

            unit:
              item.unit || "Nos",

            issued_qty:
              Number(

                item.issue_qty ||

                item.quantity ||

                item.qty ||

                0
              ),

            defective_qty: 0,
          }))
      );
    };

  // =================================================
  // UPDATE DEFECTIVE QTY
  // =================================================

  const updateDefectiveQty =
    (
      index,
      value
    ) => {

      const updated =
        [...lineItems];

      updated[index]
        .defective_qty =
          Number(value);

      setLineItems(updated);
    };

  // =================================================
  // VALIDATE
  // =================================================

  const validateForm =
    () => {

      if (
        !selectedIssue
      ) {

        alert(
          "Please select issue reference."
        );

        return false;
      }

      const valid =
        lineItems.some(
          (item) =>

            Number(
              item.defective_qty
            ) > 0
        );

      if (!valid) {

        alert(
          "Enter at least one defective quantity."
        );

        return false;
      }

      for (const item of lineItems) {

        if (

          Number(
            item.defective_qty
          ) >

          Number(
            item.issued_qty
          )

        ) {

          alert(
            `Defective qty exceeds issued qty for ${item.item_name}`
          );

          return false;
        }

        if (
          !item.item_id
        ) {

          alert(
            `Missing item_id for ${item.item_name}`
          );

          return false;
        }
      }

      return true;
    };

  // =================================================
  // SAVE DEFECTIVE RETURN
  // =================================================

  const saveDefectiveReturn =
    async () => {

      if (!validateForm())
        return;

      try {

        setLoading(true);

        const defectiveNo =
          await generateDefectiveNo();

        const validItems =
          lineItems.filter(
            (item) =>

              Number(
                item.defective_qty
              ) > 0
          );

        // =============================================
        // SAVE DEFECTIVE RETURN HEADER
        // =============================================

        await addDoc(

          collection(
            db,
            "defective_returns"
          ),

          {

            company_id:
              currentUser.company_id,

            defective_no:
              defectiveNo,

            reference_issue_id:
              selectedIssue.id,

            issue_no:
              selectedIssue.issue_no || "",

            client_id:
              selectedIssue.client_id || "",

            client_name:
              selectedIssue.client_name || "",

            project_id:
              selectedIssue.project_id || "",

            project_name:
              selectedIssue.project_name || "",

            site_id:
              selectedIssue.site_id || "",

            site_name:
              selectedIssue.site_name || "",

            items:
              validItems,

            defect_reason:
              form.defect_reason,

            notes:
              form.notes,

            created_by:
              currentUser.uid,

            created_at:
              serverTimestamp(),

            is_active: true,
          }
        );

        // =============================================
        // CENTRALIZED INVENTORY ENGINE
        // =============================================

        for (const item of validItems) {

          await defectiveReturn({

            company_id:
              currentUser.company_id,

            item_id:
              item.item_id,

            item_code:
              item.item_code || "",

            item_name:
              item.item_name,

            category:
              item.category || "",

            qty:
              Number(
                item.defective_qty
              ),

            unit:
              item.unit || "Nos",

            project_id:
              selectedIssue.project_id || "",

            project_name:
              selectedIssue.project_name || "",

            site_id:
              selectedIssue.site_id || "",

            site_name:
              selectedIssue.site_name || "",

            reference_id:
              defectiveNo,

            reference_type:
              "defective_return",

            remarks:

              `${form.defect_reason} | ${form.notes}`,
          });

          // =========================================
          // DEFECTIVE STOCK TRACKING
          // =========================================

          await addDoc(

            collection(
              db,
              "defective_stock"
            ),

            {

              company_id:
                currentUser.company_id,

              item_id:
                item.item_id,

              item_code:
                item.item_code || "",

              item_name:
                item.item_name,

              category:
                item.category || "",

              unit:
                item.unit || "Nos",

              qty:
                Number(
                  item.defective_qty
                ),

              reference_no:
                defectiveNo,

              project_id:
                selectedIssue.project_id || "",

              project_name:
                selectedIssue.project_name || "",

              site_id:
                selectedIssue.site_id || "",

              site_name:
                selectedIssue.site_name || "",

              defect_reason:
                form.defect_reason,

              notes:
                form.notes,

              created_by:
                currentUser.uid,

              created_at:
                serverTimestamp(),
            }
          );
        }

        alert(
          "Defective return saved successfully."
        );

        setSelectedIssue(
          null
        );

        setForm({

          issue_id: "",

          defect_reason: "",

          notes: "",
        });

        setLineItems([]);

      } catch (error) {

        console.error(
          "Error saving defective return:",
          error
        );

        alert(

          error.message ||

          "Failed to save defective return."
        );

      } finally {

        setLoading(false);
      }
    };

  return (

    <div className="p-6 space-y-6">

      <h1 className="text-2xl font-bold">
        Defective Return
      </h1>

      {/* ISSUE SELECT */}

      <div className="bg-white rounded-xl shadow p-4">

        <select
          className="w-full border p-2 rounded"
          value={
            form.issue_id
          }
          onChange={(e) =>
            handleIssueSelect(
              e.target.value
            )
          }
        >

          <option value="">
            Select Direct Issue
          </option>

          {issues.map(
            (issue) => (

              <option
                key={issue.id}
                value={issue.id}
              >

                {issue.issue_no}

                {" | "}

                {issue.project_name}

                {" | "}

                {issue.site_name}
              </option>
            )
          )}
        </select>
      </div>

      {/* DETAILS */}

      {selectedIssue && (

        <div className="bg-white rounded-xl shadow p-4 space-y-2">

          <p>

            <strong>
              Client:
            </strong>

            {" "}

            {
              selectedIssue.client_name
            }
          </p>

          <p>

            <strong>
              Project:
            </strong>

            {" "}

            {
              selectedIssue.project_name
            }
          </p>

          <p>

            <strong>
              Site:
            </strong>

            {" "}

            {
              selectedIssue.site_name
            }
          </p>
        </div>
      )}

      {/* ITEMS */}

      {lineItems.length > 0 && (

        <div className="bg-white rounded-xl shadow p-4 overflow-x-auto">

          <table className="min-w-full border">

            <thead className="bg-gray-100">

              <tr>

                <th className="border p-2">
                  Item
                </th>

                <th className="border p-2">
                  Unit
                </th>

                <th className="border p-2">
                  Issued Qty
                </th>

                <th className="border p-2">
                  Defective Qty
                </th>
              </tr>
            </thead>

            <tbody>

              {lineItems.map(
                (
                  item,
                  index
                ) => (

                  <tr
                    key={index}
                  >

                    <td className="border p-2">
                      {
                        item.item_name
                      }
                    </td>

                    <td className="border p-2">
                      {
                        item.unit
                      }
                    </td>

                    <td className="border p-2">
                      {
                        item.issued_qty
                      }
                    </td>

                    <td className="border p-2">

                      <input
                        type="number"
                        min="0"
                        max={
                          item.issued_qty
                        }
                        className="w-full border p-2 rounded"
                        value={
                          item.defective_qty
                        }
                        onChange={(e) =>
                          updateDefectiveQty(

                            index,

                            e.target.value
                          )
                        }
                      />
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* REASON */}

      <div className="bg-white rounded-xl shadow p-4 space-y-4">

        <input
          type="text"
          placeholder="Defect Reason"
          className="w-full border p-2 rounded"
          value={
            form.defect_reason
          }
          onChange={(e) =>
            setForm({

              ...form,

              defect_reason:
                e.target.value,
            })
          }
        />

        <textarea
          rows="3"
          placeholder="Notes"
          className="w-full border p-2 rounded"
          value={
            form.notes
          }
          onChange={(e) =>
            setForm({

              ...form,

              notes:
                e.target.value,
            })
          }
        />
      </div>

      {/* SAVE */}

      <button
        onClick={
          saveDefectiveReturn
        }
        disabled={loading}
        className="px-6 py-3 bg-red-600 text-white rounded-xl"
      >

        {

          loading

            ? "Saving..."

            : "Save Defective Return"
        }
      </button>
    </div>
  );
}